import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const RULE_MAX = 6;
const RULE_MAX_LEN = 200;
const DEFAULT_SUBTITLE =
  "We have a unique fitness gamification experience that will get you jumping, running, squatting, and more. Its a Bingo challenge, but one where the outcome is in your hands. Each week, select the highlighted workout quadrants and compete to win all the lines and a Full House. Follow the rules, upload your workout videos, and claim your victory!";

function asRuleList(value) {
  if (Array.isArray(value) && value.length > 0) return value.map((rule) => String(rule));
  return [""];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [rules, setRules] = useState([""]);
  const [step1, setStep1] = useState("");
  const [showPrompt, setShowPrompt] = useState("");
  const [subtitle, setSubtitle] = useState(DEFAULT_SUBTITLE);
  const [colors, setColors] = useState({
    text_color: "#060606",
    buttonText_color: "#ffffff",
    submit_color: "#1b3f9b",
    landing_page_title_color: "#000000",
  });

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(asRuleList(data.rules));
    setStep1(String(data.step_1 || ""));
    setShowPrompt(data.show_prompt != null ? String(data.show_prompt) : "");
    setSubtitle(String(data.landing_page_title || DEFAULT_SUBTITLE));
    setColors({
      text_color: data.text_color || "#060606",
      buttonText_color: data.buttonText_color || "#ffffff",
      submit_color: data.submit_color || "#1b3f9b",
      landing_page_title_color: data.landing_page_title_color || "#000000",
    });
  }, [data]);

  const handleColorChange = (key) => (event) => {
    setColors((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > RULE_MAX_LEN) {
      Swal.fire({ title: "Rules must below 200 characters." });
      newVal = val.slice(0, RULE_MAX_LEN);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= RULE_MAX) {
      Swal.fire({ title: "Keep 6 rules at the most." });
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire({ title: "At least 1 rule required." });
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    const cleaned = rules.map((rule) => rule.trim());
    if (cleaned.length === 0 || cleaned.some((rule) => !rule)) {
      Swal.fire({ title: cleaned.length === 0 ? "At least 1 rule required." : "Please fill in all rule fields." });
      return;
    }
    if (cleaned.length > RULE_MAX) {
      Swal.fire({ title: "Keep 6 rules at the most." });
      return;
    }
    if (cleaned.some((rule) => rule.length > RULE_MAX_LEN)) {
      Swal.fire({ title: "Rules must below 200 characters." });
      return;
    }
    if (!subtitle) {
      Swal.fire({ title: "Sub title can't be empty" });
      return;
    }
    if (subtitle.length > 350) {
      Swal.fire({ title: "Message Too Long", text: "Maximum 350 characters allowed." });
      return;
    }
    if (!step1) {
      Swal.fire({ title: "Title can't be empty" });
      return;
    }
    if (step1.length > 150) {
      Swal.fire({ title: "Message Too Long", text: "Maximum 150 characters allowed." });
      return;
    }
    const showRaw = String(showPrompt).trim();
    const show = Number(showRaw);
    if (!showRaw) {
      Swal.fire({ title: "No. of Questions to Display cannot be empty" });
      return;
    }
    if (Number.isNaN(show) || show === 0) {
      Swal.fire({ title: "No. of Questions to Display cannot be 0" });
      return;
    }
    if (show < 0) {
      Swal.fire({ title: "No. of Questions to Display cannot be negative" });
      return;
    }
    if (show > 20) {
      Swal.fire({ title: "No. of Questions to Display cannot be greater than 20" });
      return;
    }

    const payload = {
      data: {
        rules: cleaned,
        step_1: step1,
        show_prompt: String(show),
        landing_page_title: subtitle,
        text_color: colors.text_color,
        buttonText_color: colors.buttonText_color,
        submit_color: colors.submit_color,
        landing_page_title_color: colors.landing_page_title_color,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Data Updated" });
      navigate("/admin/challenges");
    } catch (error) {
      Swal.fire({ title: error.message || "Could not save rules" });
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")} type="button">
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">Colors</div>
                {[
                  ["text_color", "Text Color"],
                  ["buttonText_color", "Button Text Color"],
                  ["submit_color", "Button Background Color"],
                  ["landing_page_title_color", "Thank You Text Color"],
                ].map(([key, label]) => (
                  <div className="rules-field-row" key={key}>
                    <label className="rules-field-label">{label}</label>
                    <div className="rules-field-control">
                      <input
                        type="color"
                        className="pallet-swatch"
                        value={colors[key]}
                        onChange={handleColorChange(key)}
                      />
                      <span className="color-code">{colors[key]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">Rules</div>
                {rules.map((rule, idx) => (
                  <div className="form-group-v1" key={`rule-${idx}`}>
                    <label>Rule {idx + 1}</label>
                    <textarea
                      className="rule-input"
                      value={rule}
                      maxLength={RULE_MAX_LEN}
                      onChange={(event) => handleRuleChange(idx, event.target.value)}
                    />
                    <button type="button" className="remove-button" onClick={() => handleRemoveRule(idx)}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="back-button" onClick={handleAddRule}>
                  Add rule
                </button>
                <div className="code">* Keep 6 rules at the most. Character limit of each rule is 200.</div>

                <div className="form-group-v1">
                  <label>Title</label>
                  <textarea
                    className="rule-input"
                    value={step1}
                    maxLength={150}
                    onChange={(event) => setStep1(event.target.value)}
                  />
                  <div className="code">* Maximum character limit should be 150 characters</div>
                </div>

                <div className="form-group-v1">
                  <label>No. of Questions to Display</label>
                  <input
                    className="rule-input"
                    value={showPrompt}
                    onChange={(event) => setShowPrompt(event.target.value)}
                  />
                </div>

                <input type="hidden" value={subtitle} readOnly />
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" type="button" onClick={handleSubmit}>
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
